import { Modal, Typography } from 'antd';
import { aiAgentCenterMessages, messages, ticketMessages } from 'messages';
import React, { useEffect, useState } from 'react';
import ContactTicketList from './ContactTicketList';
import { useLazyTicketsAgent, useTicketDetail } from 'hooks/tickets';
import { TICKET_STATUS_OPTION } from 'utils';
import { twMerge } from 'tailwind-merge';
import TicketDetailed from 'components/ticket-detailed/TicketDetailed';

type Props = {
  chatSessionId?: number;
  callId?: string;
  className?: string;
  titleClassName?: string;
};
const ChatSessionTickets: React.FC<Props> = ({ chatSessionId, callId, className, titleClassName }) => {
  const { fetchData, isLoading, isFetching, groupedByStatusId, count, hasMore, handleLoadMore } = useLazyTicketsAgent();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [isSecondCall, setIsSecondCall] = useState(false);

  useEffect(() => {
    if (chatSessionId) {
      fetchData({ chatSessionId: chatSessionId });
    } else if (callId) {
      fetchData({ callId: callId });
    }
  }, [chatSessionId, callId]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedTicketId(null);
    setIsSecondCall(false);
  };

  const showModal = (ticketId: number, secondCall: boolean = false) => {
    setIsModalOpen(true);
    setSelectedTicketId(ticketId);
    setIsSecondCall(secondCall);
  };

  const { ticketDetail } = useTicketDetail(selectedTicketId || 0);

  return (
    <div className={twMerge('px-5 pb-6 pt-5', className)}>
      <Typography.Text className={twMerge('font-semibold', titleClassName)}>
        {aiAgentCenterMessages.ticketCreated}
      </Typography.Text>
      <div className='mt-5'>
        {isLoading || isFetching ? (
          <div className='flex flex-col gap-1'>
            <div className='skeleton-active h-4 w-[200px] rounded-sm'></div>

            <div className='skeleton-active mt-3 h-4 w-[300px] rounded-sm'></div>
            <div className='skeleton-active h-4 w-full rounded-sm'></div>
          </div>
        ) : (
          <>
            {count > 0 ? (
              Object.entries(groupedByStatusId).map(([statusId, tickets]) => {
                const statusName = tickets[0]?.status?.name;

                const color = statusId
                  ? TICKET_STATUS_OPTION[statusId as unknown as keyof typeof TICKET_STATUS_OPTION]?.color || '#1564E8'
                  : '#1564E8';
                return (
                  <div key={statusId} className='mb-6'>
                    <Typography.Paragraph
                      className='mb-4 border-l-4 px-2 text-xs font-semibold uppercase leading-4'
                      style={{
                        borderColor: color
                      }}
                      type='secondary'
                    >
                      {statusName || '-'}
                    </Typography.Paragraph>
                    <ContactTicketList data={tickets} showModal={showModal} />
                  </div>
                );
              })
            ) : (
              <div>
                <Typography.Text className='text-colorTextPlaceholder'>{ticketMessages.notHaveTicket}</Typography.Text>
              </div>
            )}
          </>
        )}
        {hasMore && !isLoading && !isFetching && (
          <div className='my-4'>
            <Typography.Link className='text-sm' onClick={handleLoadMore}>
              {messages.LoadMore}
            </Typography.Link>
          </div>
        )}
      </div>
      <Modal
        className='ant-modal-body-p-none max-h-[1000px] w-[1088px]'
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        centered
      >
        <TicketDetailed
          status={ticketDetail?.status.code}
          ticketId={selectedTicketId || 0}
          cancelDetailModal={handleCancel}
          isSecondCall={isSecondCall}
        />
      </Modal>
    </div>
  );
};

export default ChatSessionTickets;
